import {useMemo, useEffect, useRef} from "react";
import {useGetFeed, useGetMe, useRequestAutoEnrollment, ScheduledModuleDto} from "@/app2/shared/data";
import {getDateInTimezone} from "@/app2/shared/utils/dateManipulation";
import {TodayLearningModuleDto} from "../types";

/**
 * Maps ScheduledModuleDto from the API to TodayLearningModuleDto for the UI
 */
const mapScheduledModuleToTodayLearning = (
	module: ScheduledModuleDto
): TodayLearningModuleDto => {
	// Extract course data (handle unknown type)
	const course = module.course as any;

	return {
		id: module.id,
		title: module.title,
		slug: module.slug,
		subtitle: module.subtitle || "",
		type: module.moduleType as "lesson" | "quiz" | "survey",
		moduleType: module.moduleType,
		videoUrl: typeof module.videoUrl === "string" ? module.videoUrl : undefined,
		videoThumbnailUrl: typeof module.videoThumbnailUrl === "string" ? module.videoThumbnailUrl : undefined,
		videoDuration: typeof module.videoLength === "number" ? module.videoLength : undefined,
		content: module.content || "",
		complete: module.complete,
		progression: module.progression ? true : false,
		dueDate: module.dueDate ? new Date(module.dueDate) : null,
		availableFrom: module.availableFrom ? new Date(module.availableFrom as any) : new Date(),
		startedAt: module.startedAt ? new Date(module.startedAt as any) : null,
		finishedAt: module.finishedAt ? new Date(module.finishedAt as any) : null,
		courseId: course?.id || 0,
		courseTitle: course?.title || "",
		courseSlug: course?.slug || "",
		courseEnrollmentType: module.courseEnrollmentType,
		enrollmentType: module.enrollmentType,
		enrollmentId: module.enrollmentId,
		duration: typeof module.duration === "number" ? module.duration : 0,
		isLocked: module.isLocked || false // Pass through locked status for linear progression
	};
};

interface UseTodayLearningResult {
	todayLearning: TodayLearningModuleDto[] | null;
	isLoading: boolean;
	isError: boolean;
	error: Error | null;
	refetch: () => void;
	/** True when user completed all modules today (for empty state messaging) */
	completedSomethingToday: boolean;
	/** IANA timezone for due-date display (ME profile or browser) */
	displayTimezone: string;
}

/**
 * Feature-specific hook for Today's Learning section
 *
 * Uses get-feed API: overdues first (to the left), then upcoming lessons up to daily limit.
 *
 * - Data layer: shared/data/repos/user.ts (useGetFeed)
 * - Business logic: this hook (mapping to UI format)
 */
export const useTodayLearning = (locale?: string, startDate?: string): UseTodayLearningResult => {
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const today = startDate ?? getDateInTimezone(new Date(), timezone);
	const resolvedLocale = locale === 'en' || locale === 'es-419' ? locale : 'en';

	const {data, isLoading, isError, isSuccess, error, refetch} = useGetFeed(
		{
			startDate: today,
			timezone,
			locale: resolvedLocale,
		},
		{ enabled: locale != null },
	);

	const {data: me, isLoading: isMeLoading} = useGetMe();
	const {mutate: requestAutoEnrollment} = useRequestAutoEnrollment();
	const hasRequestedAutoEnrollment = useRef(false);

	const userTimezone = me?.timezone ?? timezone;
	const completedSomethingToday = useMemo(() => {
		const completedAt = me?.latestLessonCompletedAt;
		if (!completedAt) return false;
		const todayStr = getDateInTimezone(new Date(), userTimezone);
		const completedDateStr = getDateInTimezone(new Date(completedAt), userTimezone);
		return todayStr === completedDateStr;
	}, [me?.latestLessonCompletedAt, userTimezone]);

	const todayLearning = useMemo(() => {
		const items = data?.items ?? [];
		return items.map(mapScheduledModuleToTodayLearning);
	}, [data]);

	// Request auto-enrollment only when feed and me have fully resolved and user is definitively in empty state
	useEffect(() => {
		const isDefinitivelyEmpty =
			isSuccess &&
			!isMeLoading &&
			data !== undefined &&
			(data.items?.length ?? 0) === 0 &&
			!completedSomethingToday;

		if (isDefinitivelyEmpty && !hasRequestedAutoEnrollment.current) {
			hasRequestedAutoEnrollment.current = true;
			requestAutoEnrollment();
		}
	}, [isSuccess, isMeLoading, data, completedSomethingToday, requestAutoEnrollment]);

	return {
		todayLearning,
		isLoading,
		isError,
		error: error as Error | null,
		refetch,
		completedSomethingToday,
		displayTimezone: userTimezone,
	};
}
